import { useState } from 'react';
import { ClipboardList, FilePlus2 } from 'lucide-react';
import { Button, Card, CardBody, EmptyState, ErrorState, LoadingState, PageHeader, cn } from '@tutor/ui';
import { useHomeworkAssignments, type HomeworkFilters } from '../../hooks/queries';
import { HomeworkCard } from '../../components/HomeworkCard';
import { SetHomeworkDialog } from '../../components/dialogs/SetHomeworkDialog';

const TUTOR_SCOPES = [
  { value: 'to-mark', label: 'To mark' },
  { value: 'due', label: 'Outstanding' },
  { value: 'marked', label: 'Marked' },
  { value: 'all', label: 'All' },
] as const;

const STUDENT_SCOPES = [
  { value: 'due', label: 'Due' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'marked', label: 'Marked' },
  { value: 'all', label: 'All' },
] as const;

export function HomeworkListPage({ perspective }: { perspective: 'tutor' | 'student' }) {
  const scopes = perspective === 'tutor' ? TUTOR_SCOPES : STUDENT_SCOPES;
  const [scope, setScope] = useState<HomeworkFilters['scope']>(scopes[0].value);
  const [setOpen, setSetOpen] = useState(false);

  const { data, isLoading, isError } = useHomeworkAssignments({ scope, pageSize: 50 });

  return (
    <>
      <PageHeader
        title="Homework"
        description={
          perspective === 'tutor'
            ? 'Work you have set, and what is waiting to be marked.'
            : 'Everything your tutors have set you.'
        }
        action={
          perspective === 'tutor' ? (
            <Button onClick={() => setSetOpen(true)} leftIcon={<FilePlus2 className="h-4 w-4" aria-hidden />}>
              Set homework
            </Button>
          ) : undefined
        }
      />

      {perspective === 'tutor' && <SetHomeworkDialog open={setOpen} onClose={() => setSetOpen(false)} />}

      <div className="mb-4 flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1">
        {scopes.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setScope(option.value)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              scope === option.value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      <Card>
        <CardBody className="space-y-3">
          {isLoading ? (
            <LoadingState />
          ) : isError ? (
            <ErrorState description="We could not load homework." />
          ) : (data?.items.length ?? 0) === 0 ? (
            <EmptyState
              title="Nothing here"
              description={
                perspective === 'tutor' ? 'No homework matches this filter.' : 'You are all caught up.'
              }
              icon={<ClipboardList className="h-5 w-5" aria-hidden />}
            />
          ) : (
            data!.items.map((assignment) => (
              <HomeworkCard key={assignment.id} assignment={assignment} perspective={perspective} />
            ))
          )}
        </CardBody>
      </Card>
    </>
  );
}
